import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {UsersActions} from "../actions";
import {catchError, map, mergeMap} from "rxjs";
import {ApiError, UsersService, UserStatusEnum} from "@esa/api";
import {ToastrService} from "ngx-toastr";
import {Router} from "@angular/router";
import {ApiErrorHandlerService} from "../../services";
import {HttpStatusCode} from "@angular/common/http";
import {Store} from "@ngrx/store";

@Injectable()
export class UsersEffects {

  getUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.getUsers),
      mergeMap((action) => this._usersService.getApiUser(action.pageNumber, action.pageSize, null, action.userStatus).pipe(
        map(response =>
           UsersActions.getUsersSuccess({ users: response.data, totalUsers: response.totalCount })
        ),
          catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, UsersActions.getUsersError());
        })
      ))
    );
  });

  getLoggedInUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.getLoggedInUser),
      mergeMap(({}) => this._usersService.getApiUserProfile().pipe(
        map(response => {
          if(response == null || response.userStatus == UserStatusEnum.DISABLED) {
            this.store.dispatch(UsersActions.setUserDisabled({ userDisabled: true }));
            this.router.navigate(['/', { outlets: { userNotFound: ['user-not-found'] } }]);
          }
          return UsersActions.getLoggedInUserSuccess({ loggedInUser: response })
        }),
        catchError((error: ApiError) => {
          if(error.body.status == HttpStatusCode.NotFound && error.body.detail == "Could not find User") {
            this.router.navigate(['/', { outlets: { userNotFound: ['user-not-found'] } }]);
          }
          return this.apiErrorHandlerService.handleError(error, UsersActions.getLoggedInUserError());
        })
      ))
    );
  });

  getUserList$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.getUserList),
      mergeMap((action) => this._usersService.getApiUserList(action?.fiuId, action?.userStatus).pipe(
        map(response => UsersActions.getUserListSuccess({ userList: response })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, UsersActions.getUserListError());
        })
      ))
    );
  });

  createUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.createUser),
      mergeMap(({user}) => this._usersService.postApiUser(user).pipe(
        map(response => {
          this.toastrService.success('User created successfully');
          this.router.navigateByUrl('users');
          return UsersActions.createUserSuccess();
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, UsersActions.createUserError());
        })
      ))
    );
  });

  setSelectedUserId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.setSelectedUserId),
      map(({ selectedUserId }) => UsersActions.getUserById({ id: selectedUserId }))
    );
  });

  getUserById$ = createEffect( () => {
    return this.actions$.pipe(
      ofType(UsersActions.getUserById),
      mergeMap(({ id }) => this._usersService.getApiUser1(id).pipe(
        map(selectedUser => UsersActions.getUserByIdSuccess({ selectedUser })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, UsersActions.getUserByIdError());
        })
      ))
    )
  });

  updateUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.updateUser),
      mergeMap(({ user }) => this._usersService.putApiUser(user).pipe(
        map(response => {
          this.toastrService.success('User updated successfully');
          this.router.navigateByUrl('users');
          return UsersActions.updateUserSuccess();
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, UsersActions.updateUserError());
        })
      ))
    );
  });

  deleteUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.deleteUser),
      mergeMap(( action ) => this._usersService.putApiUser(action.user).pipe(
        map(() => {
          this.toastrService.success('User disabled successfully');
          return UsersActions.deleteUserSuccess({
            pageNumber : action.pageNumber,
            pageSize: action.pageSize
          });
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, UsersActions.deleteUserError());
        })
        ))
      );
    })

  loadUsersOnDeleteUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.deleteUserSuccess),
      map((action) => UsersActions.getUsers({
        pageNumber: action.pageNumber,
        pageSize: action.pageSize,
        userStatus: 'Active' as UserStatusEnum
      }))
    );
  });


  reactivateUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.reactivateUser),
      mergeMap((action) => this._usersService.putApiUser(action.user).pipe(
        map(() => {
          this.toastrService.success('User enabled successfully');
          return UsersActions.reactivateUserSuccess( {
            pageNumber: action.pageNumber,
            pageSize: action.pageSize
          });
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, UsersActions.reactivateUserError());
        })
      ))
    );
  })

  loadUsersOnReactivateUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UsersActions.reactivateUserSuccess),
      map((action) => UsersActions.getUsers({
        pageNumber: action.pageNumber,
        pageSize: action.pageSize,
        userStatus: 'Disabled' as UserStatusEnum
      }))
    );
  });

 resendUserInvitation$ = createEffect(() => {
   return this.actions$.pipe(
     ofType(UsersActions.resendUserInvitation),
     mergeMap(({user}) => this._usersService.putApiUserInvite(user).pipe(
       map(() => {
         this.toastrService.success('User re-invited successfully');
         this.router.navigateByUrl('users');
         return UsersActions.resendUserInvitationSuccess();
       }),
       catchError((error: ApiError) => {
         return this.apiErrorHandlerService.handleError(error, UsersActions.resendUserInvitationError)
       })
     ))
   );
 })


  constructor(
    private actions$: Actions,
    private _usersService: UsersService,
    private toastrService: ToastrService,
    private router: Router,
    private store: Store,
    private apiErrorHandlerService: ApiErrorHandlerService
  ) {}
}
